import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useGetApprovalTicket } from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import type { GetApprovalTicketFormValuesT } from '../declarations';

type UseTicketApprovePopupT = {
  isApprovingTicket: boolean;
  handleSubmitForm: (data: GetApprovalTicketFormValuesT, onClose: NoopT) => void;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useTicketApprovePopup = (ticketId: string): UseTicketApprovePopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  /**
   * Api hooks
   */
  const getApprovalTicketMutation = useGetApprovalTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
    },
  });

  /**
   * Handlers
   */
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
    },
    []
  );

  const handleSubmitForm = useCallback(
    (data: GetApprovalTicketFormValuesT, onClose: NoopT) => {
      getApprovalTicketMutation.mutate(
        { ...data, ticketIds: [ticketId] },
        {
          onSuccess: () => {
            dispatch(
              updateSnackbarNotification({
                snackbarMessage: 'Approval requested successfully',
                snackBarSeverity: 'success',
              })
            );
            onClose();
          },
          onError: error => {
            const errorMsg =
              getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
            dispatch(
              updateSnackbarNotification({
                snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
                snackBarSeverity: 'error',
              })
            );
            onClose();
          },
        }
      );
    },
    [getApprovalTicketMutation, ticketId, dispatch]
  );

  return {
    isApprovingTicket: getApprovalTicketMutation.isLoading,
    handleSubmitForm,
    handleClosePopup,
  };
};

export default useTicketApprovePopup;
